import type {
  ImportIssue,
  ImportItemStatus,
  ImportOverrides,
  ImportPerson,
  ImportPersonOverride,
  ImportReport,
  ImportSet,
  ImportTableColumn,
  IssueReason,
  TableTargetField,
} from '@lite-workspace/import-pipeline-core';
import type { ImportRun } from '../infrastructure/person-import-api.service';
import type { Person } from '../entities/project.models';

/** Применён ли прогон: сервер присылает отчёт и упавшему apply, поэтому один report ничего не значит. */
export function importRunApplied(run: Pick<ImportRun, 'status' | 'report'> | null | undefined): boolean {
  return !!run?.report && run.status !== 'FAILED';
}

export type ImportNameField = 'surname' | 'name' | 'fatherName' | 'subject';
export const IMPORT_NAME_FIELDS: readonly ImportNameField[] = ['surname', 'name', 'fatherName', 'subject'];

export type ImportPersonType = ImportPerson['type'];

export const PERSON_TYPE_LABEL: Record<ImportPersonType, string> = {
  student: 'Ученик',
  teacher: 'Педагог',
};

export const ISSUE_REASON_LABEL: Record<IssueReason, string> = {
  unparsed: 'имя из названия не разобрано',
  ambiguous_name: 'имя совпадает с несколькими в проекте',
  duplicate_in_source: 'дубль в исходных файлах',
  group_photo: 'групповой кадр — несколько лиц',
  no_face: 'лицо не найдено',
  face_timeout: 'распознавание не успело',
  face_owned: 'у человека на этом кадре уже стоит другое имя',
  face_conflict: 'на кадрах один человек, а имён несколько',
  vignette_reserved: 'кадр занят виньеткой',
  skipped_extra_photo: 'лишнее фото пропущено',
  quotes_disabled: 'цитаты в проекте выключены',
  quote_too_long: 'цитата длиннее рамки',
  encoding: 'кодировка имени не распозналась',
  file_not_found: 'файл не найден',
  file_name_collision: 'несколько файлов с таким именем — кадр не подставлен',
  row_not_found: 'строка не найдена',
  too_small: 'файл слишком мал',
};

export const ITEM_STATUS_LABEL: Record<ImportItemStatus, string> = {
  CREATED: 'Создано',
  UPDATED: 'Обновлено',
  MATCHED: 'Совпало с проектом',
  SKIPPED: 'Пропущено',
  PENDING_FACE: 'Ждут распознавание',
  BOUND: 'Привязано',
  UNMATCHED: 'Без совпадения',
  FAILED: 'Не удалось',
};

/** Порядок счётчиков в итоге: сначала то, что сделано, потом то, что ждёт, потом отказы. */
const ITEM_STATUS_ORDER: readonly ImportItemStatus[] = [
  'CREATED',
  'UPDATED',
  'MATCHED',
  'BOUND',
  'PENDING_FACE',
  'SKIPPED',
  'UNMATCHED',
  'FAILED',
];

export interface ImportPreviewFile {
  key: string;
  name: string;
  /** Статус связи по-русски: «портрет» / «фото» или причина из issues файла. */
  status: string;
  /** Есть проблема (issue) — строка подсвечивается тоном warn. */
  warn: boolean;
}

export interface ImportPreviewRow {
  key: string;
  type: ImportPersonType;
  fields: Record<ImportNameField, string>;
  /** Поля, которые парсер вывел сам (origin=derived) — бейдж «подставлено». */
  derived: ReadonlySet<ImportNameField | 'type'>;
  match: { status: 'none' | 'matched' | 'ambiguous'; personId?: string; candidates: string[] };
  /** Выбор человека: undefined — как предложено; id — кандидат; null — принудительно новая. */
  matchPersonId?: string | null;
  excluded: boolean;
  files: ImportPreviewFile[];
}

const LINK_KIND_LABEL = { portrait: 'портрет', photo: 'фото' } as const;

function fileStatus(set: ImportSet, fileKey: string, kind: 'portrait' | 'photo'): Pick<ImportPreviewFile, 'status' | 'warn'> {
  const issue = set.issues.find(item => item.scope === 'file' && item.key === fileKey);
  if (issue) return { status: ISSUE_REASON_LABEL[issue.reason], warn: true };
  return { status: LINK_KIND_LABEL[kind], warn: false };
}

function nameFieldsOf(person: ImportPerson): Record<ImportNameField, string> {
  return {
    surname: person.fields.surname ?? '',
    name: person.fields.name ?? '',
    fatherName: person.fields.fatherName ?? '',
    subject: person.fields.subject ?? '',
  };
}

/** Строки предпросмотра из набора: по одной на персону, файлы — по связям персоны. */
export function previewRows(set: ImportSet): ImportPreviewRow[] {
  const fileByKey = new Map(set.files.map(file => [file.key, file]));
  return set.persons.map(person => {
    const derived = new Set<ImportNameField | 'type'>();
    for (const field of [...IMPORT_NAME_FIELDS, 'type'] as const) {
      if (person.provenance[field]?.origin === 'derived') derived.add(field);
    }

    const files = set.links
      .filter(link => link.personKey === person.key)
      .map(link => {
        const file = fileByKey.get(link.fileKey);
        return { key: link.fileKey, name: file?.originalName ?? link.fileKey, ...fileStatus(set, link.fileKey, link.kind) };
      });

    return {
      key: person.key,
      type: person.type,
      fields: nameFieldsOf(person),
      derived,
      match: {
        status: person.match?.status ?? 'none',
        personId: person.match?.personId,
        candidates: person.match?.candidates ?? [],
      },
      excluded: false,
      files,
    };
  });
}

/**
 * Правки предпросмотра → overrides для apply: только то, что отличается от набора, иначе сервер
 * пометит origin=manual и нетронутые поля. Пустой diff — пустой объект (apply без правок).
 */
export function buildOverrides(set: ImportSet, rows: readonly ImportPreviewRow[]): ImportOverrides {
  const byKey = new Map(set.persons.map(person => [person.key, person]));
  const persons: Record<string, ImportPersonOverride> = {};

  for (const row of rows) {
    const source = byKey.get(row.key);
    if (!source) continue;
    const override: ImportPersonOverride = {};

    const fields: Partial<ImportPerson['fields']> = {};
    for (const field of IMPORT_NAME_FIELDS) {
      const next = row.fields[field].trim();
      if (next !== (source.fields[field] ?? '')) fields[field] = next;
    }

    if (Object.keys(fields).length > 0) override.fields = fields;
    if (row.type !== source.type) override.type = row.type;
    if (row.matchPersonId !== undefined && row.matchPersonId !== source.match?.personId) {
      override.matchPersonId = row.matchPersonId;
    }

    if (row.excluded) override.exclude = true;
    if (Object.keys(override).length > 0) persons[row.key] = override;
  }

  return Object.keys(persons).length > 0 ? { persons } : {};
}

/**
 * Подписи под кадрами: ключ файла → имя человека. Связь «кадр → персона» есть только в наборе
 * прогона (`links`), ни `Photo`, ни ответ `/photos` её не несут. Ключ связи — id файла проекта,
 * тот же, что у плитки.
 */
export function captionsByFile(set: Pick<ImportSet, 'links' | 'persons'>): Map<string, string> {
  const nameByPerson = new Map(set.persons.map(person => [person.key, rowDisplayName({ fields: nameFieldsOf(person) })]));
  const captions = new Map<string, string>();
  for (const link of set.links) {
    const name = nameByPerson.get(link.personKey);
    if (name) captions.set(link.fileKey, name);
  }

  return captions;
}

/** Роли разобранного набора: сколько учеников и сколько педагогов в нём вышло. */
export function personRoleCounts(set: Pick<ImportSet, 'persons'>): { students: number; teachers: number } {
  const teachers = set.persons.filter(person => person.type === 'teacher').length;
  return { students: set.persons.length - teachers, teachers };
}

export function rowDisplayName(row: Pick<ImportPreviewRow, 'fields'>): string {
  return [row.fields.surname, row.fields.name, row.fields.fatherName]
    .map(part => part.trim())
    .filter(Boolean)
    .join(' ');
}

/** Текст проблемы по-русски: «Файл IMG_0001.jpg: имя из названия не разобрано». */
export function issueText(issue: ImportIssue, set?: Pick<ImportSet, 'files' | 'persons'>): string {
  const label = ISSUE_REASON_LABEL[issue.reason] ?? issue.reason;
  let subject: string;
  if (issue.scope === 'file') {
    const file = set?.files.find(item => item.key === issue.key);
    subject = `Файл ${file?.originalName ?? issue.key}`;
  } else if (issue.scope === 'person') {
    const person = set?.persons.find(item => item.key === issue.key);
    const name = person ? rowDisplayName({ fields: nameFieldsOf(person) }) : '';
    subject = name || `Персона ${issue.key}`;
  } else if (issue.key === 'rest' && issue.detail) {
    // Сводная запись хвоста за потолком проблем (parsers/table.ts): её субъект — сам счёт
    // «ещё N строк», служебный ключ человеку не показывается.
    const tail: string = issue.detail.replace(/^и\s+/u, '');
    return `${tail.charAt(0).toUpperCase()}${tail.slice(1)}: ${label}`;
  } else {
    subject = `Строка ${issue.key}`;
  }

  return issue.detail ? `${subject}: ${label} (${issue.detail})` : `${subject}: ${label}`;
}

export interface IssueLine {
  id: string;
  text: string;
}

/**
 * Строки проблем с ключом для `@for track`. Один текст встречается дважды на законных основаниях — два файла с
 * одинаковым originalName и той же причиной, два одноимённых человека с duplicate_in_source, — поэтому трекать
 * по самой строке нельзя: Angular роняет NG0955 «duplicate keys». Ключ собирается из адреса проблемы
 * (scope+key+reason), а порядковый номер добивает уникальность там, где адрес всё-таки совпал (issue.detail).
 */
export function issueLines(issues: readonly ImportIssue[], set?: Pick<ImportSet, 'files' | 'persons'>): IssueLine[] {
  return issues.map((issue: ImportIssue, index: number) => ({
    id: `${index}|${issue.scope}|${issue.key}|${issue.reason}`,
    text: issueText(issue, set),
  }));
}

export interface ReportCounter {
  status: ImportItemStatus;
  label: string;
  count: number;
}

/** Ненулевые счётчики отчёта в порядке показа. */
export function reportCounters(report: ImportReport): ReportCounter[] {
  return ITEM_STATUS_ORDER.map(status => ({
    status,
    label: ITEM_STATUS_LABEL[status],
    count: report.counters?.[status] ?? 0,
  })).filter(item => item.count > 0);
}

export interface ImportRowCounts {
  /** N — строк источника: по одной на персону набора. */
  total: number;
  /** K — строк, чьё имя село на человека. */
  matched: number;
  /** M — строк, чьё имя не легло: без кадра или по другой причине. */
  unmatched: number;
  /** Имена всех неподставленных строк, в порядке показа: сначала без фото, затем остальные. */
  unmatchedNames: string[];
  /** Имена строк без кадра (file_not_found). */
  missingNames: string[];
  /** Имена строк с кадром, чьё имя всё же не легло: спорное имя, занятое лицо, групповой кадр и т. п. */
  unplacedNames: string[];
}

/** ФИО всех строк таблицы — что она принесла, независимо от того, село ли имя на человека. */
export function tableNames(set: ImportSet): string[] {
  return previewRows(set).map(rowDisplayName).filter(Boolean);
}

/**
 * Причины UNMATCHED строки ЗАПИСИ (upsertPerson в apply-import, settleRow с ключом персоны в
 * resolve-pending-links): имя человеку не досталось. Цитатные причины сюда не входят — они
 * информационные, имя при них подставлено; row_not_found пишется только в строку связи.
 */
const UNPLACED_REASONS: ReadonlySet<IssueReason> = new Set<IssueReason>([
  'file_not_found',
  'file_name_collision',
  'ambiguous_name',
  'face_conflict',
  'face_owned',
  'group_photo',
  'no_face',
  'face_timeout',
]);

/**
 * «K из N, M не подставлено» — счёт по СТРОКАМ. `report.counters` для него не годится: он мешает
 * персон и связи в одних и тех же числах. Неподставленная строка — проблема scope=person с причиной
 * из UNPLACED_REASONS, её ключ и есть ключ строки предпросмотра, по нему берётся имя. Один ключ
 * считается один раз; «без фото» — только file_not_found, остальное — «имя не легло».
 */
export function rowCounts(rows: readonly ImportPreviewRow[], issues: readonly ImportIssue[]): ImportRowCounts {
  // Спорное имя файла: на apply строка получает тот же file_not_found, что и строка без кадра, но
  // кадры в проекте лежат, и догрузка её не лечит — это «имя не легло», иначе повтор применения
  // взводился бы на каждом маунте без конца.
  const collided = new Set<string>(
    issues.flatMap(issue => (issue.reason === 'file_name_collision' && issue.personKey ? [issue.personKey] : [])),
  );
  const reasonByKey = new Map<string, IssueReason>();
  for (const issue of issues) {
    if (issue.scope !== 'person' || !UNPLACED_REASONS.has(issue.reason)) continue;
    const reason: IssueReason =
      issue.reason === 'file_not_found' && collided.has(issue.key) ? 'file_name_collision' : issue.reason;
    // Строка без кадра остаётся «без фото», какая бы причина ни встретилась рядом с ней.
    if (reason === 'file_not_found' || !reasonByKey.has(issue.key)) reasonByKey.set(issue.key, reason);
  }

  const nameOf = (row: ImportPreviewRow): string => rowDisplayName(row) || row.key;
  const missingNames = rows.filter(row => reasonByKey.get(row.key) === 'file_not_found').map(nameOf);
  const unplacedNames = rows.filter(row => reasonByKey.has(row.key) && reasonByKey.get(row.key) !== 'file_not_found').map(nameOf);
  const unmatchedNames = [...missingNames, ...unplacedNames];

  return {
    total: rows.length,
    matched: rows.length - unmatchedNames.length,
    unmatched: unmatchedNames.length,
    unmatchedNames,
    missingNames,
    unplacedNames,
  };
}

/** Русская форма «файл» по числу: 1 файл · 2-4 файла · 5+ файлов. */
export function fileWord(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'файл';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'файла';
  return 'файлов';
}

// ——— Табличный источник: что принимаем ———

/** Расширения таблицы, которые читаем: Google Sheets отдаёт CSV, Excel — CSV/TSV, TSV берёт тот же парсер. */
export const TABLE_EXTENSIONS: readonly string[] = ['csv', 'tsv', 'txt'];

/** Расширение решает, MIME только страхует: CSV из выгрузок приезжает и с пустым типом. */
export const TABLE_MIME_PREFIXES: readonly string[] = ['text/'];

/**
 * Форматы таблиц, которые мы не читаем и не грузим. `.xls` здесь обязателен: его MIME —
 * `application/vnd.ms-excel`, то есть без чёрного списка он ушёл бы в парсер как CSV.
 */
export const REFUSED_TABLE_EXTENSIONS: readonly string[] = ['xlsx', 'xls', 'numbers', 'gsheet'];

export function refusedExtensionOf(file: File): string | null {
  const name = file.name.toLowerCase();
  return REFUSED_TABLE_EXTENSIONS.find(extension => name.endsWith(`.${extension}`)) ?? null;
}

/** Заголовок отказа по формату — он же тост: сначала называем формат, дальше зовём в CSV. */
export function refusedFormatTitle(extension: string): string {
  return `Формат .${extension} не поддерживается`;
}

// ——— Табличный источник: шаг «колонки» ———

/** Порядок целей в селекте колонки — от того, что называет человека, к служебному. */
export const TABLE_TARGET_OPTIONS: readonly TableTargetField[] = [
  'surname',
  'name',
  'fatherName',
  'fullName',
  'class',
  'role',
  'subject',
  'description',
  'phone',
  'email',
  'file',
  'skip',
];

export const TABLE_TARGET_LABEL: Record<TableTargetField, string> = {
  surname: 'Фамилия',
  name: 'Имя',
  fatherName: 'Отчество',
  fullName: 'ФИО',
  class: 'Класс',
  role: 'Роль',
  subject: 'Предмет',
  description: 'Цитата',
  phone: 'Телефон',
  email: 'Email',
  file: 'Имя файла',
  skip: 'Не использовать',
};

export interface ImportColumnRow {
  index: number;
  header: string;
  target: TableTargetField;
  /** Заголовок узнан не точно (0.7) — подпись «похоже на», человек проверяет глазами. */
  guessed: boolean;
  sample: string;
}

export function tableColumnRows(set: ImportSet): ImportColumnRow[] {
  return (set.table?.columns ?? []).map((column: ImportTableColumn) => ({
    index: column.index,
    header: column.header,
    target: column.target,
    guessed: column.target !== 'skip' && column.confidence < 1,
    sample: column.sample ?? '',
  }));
}

/** Правки шага «колонки» → маппинг для повторного предпросмотра и для apply. */
export function tableMapping(rows: readonly ImportColumnRow[]): Record<string, TableTargetField> {
  return Object.fromEntries(rows.map(row => [row.header, row.target]));
}

/** Импорт нечего применять, пока не назначена колонка с именем: без неё персона не соберётся. */
export function hasNameColumn(rows: readonly ImportColumnRow[]): boolean {
  return rows.some(row => row.target === 'fullName' || row.target === 'surname' || row.target === 'name');
}

/** Импорт привязывает строку к кадру по имени файла: без этой колонки apply отдаёт каждую строку в file_not_found. */
export function hasFileColumn(rows: readonly ImportColumnRow[]): boolean {
  return rows.some(row => row.target === 'file');
}

/** «К импорту»: строки без кадра apply не сажает ни на кого (file_not_found), поэтому в счёт не входят. */
export function importableCount(rows: readonly ImportPreviewRow[]): number {
  return rows.filter(row => !row.excluded && row.files.length > 0).length;
}

/**
 * Ключ сверки имени: фамилия и имя без регистра, ё→е и лишних пробелов. Отчество в ключ не входит —
 * таблица часто несёт два слова, а ростер все три, и строгое сравнение объявляло бы своих чужими.
 */
export function nameMatchKey(fullName: string): string {
  const [surname = '', name = ''] = fullName.toLowerCase().replace(/ё/g, 'е').trim().split(/\s+/);

  return `${surname} ${name}`.trim();
}

/** Тот же ключ для персоны проекта: разобранных ФИО может не быть — тогда режем `name` по словам. */
export function personNameKey(person: Person): string {
  const parsed = `${person.surname ?? ''} ${person.firstName ?? ''}`.trim();

  return nameMatchKey(parsed || (person.name ?? ''));
}
